<template>
  <div class="my-orders-page">
    <div class="container">
      <div class="page-header">
        <h1>My Orders</h1>
        <p>View and track your order history</p>
      </div>

      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        Loading your orders...
      </div>

      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          ⚠️
        </div>
        <p>
          {{ error }}
        </p>
      </div>

      <div v-else-if="orders.length === 0" class="empty-state">
        <div class="empty-icon">
          📦
        </div>
        <h2>
          No orders yet
        </h2>
        <p>You haven't placed any orders yet. Start shopping!</p>
        <button class="btn-shop" @click="$router.push('/shop')">
          Browse Products
        </button>
      </div>

      <div v-else class="orders-list">
        <div
          v-for="order in orders"
          :key="order.id"
          class="order-card"
          @click="viewOrder(order.id)"
        >
          <div class="order-header">
            <div class="order-info">
              <h3>
                Order #{{ order.order_number }}
              </h3>
              <p class="order-date">
                {{ formatDate(order.created_at) }}
              </p>
            </div>
            <div class="order-status">
              <span :class="['status-badge', order.status]">
                {{ formatStatus(order.status) }}
              </span>
            </div>
          </div>

          <div class="order-items-preview">
            <div
              v-for="item in order.items.slice(0, 3)"
              :key="item.id"
              class="item-preview"
            >
              <div class="item-image-small">
                <img
                  v-if="item.product && item.product.images && item.product.images.length > 0"
                  :src="getPrimaryImage(item.product).image_url"
                  :alt="item.product_name"
                />
                <div v-else class="no-image-small">
                  ?
                </div>
              </div>
            </div>
            <span v-if="order.items.length > 3" class="more-items">
              +{{ order.items.length - 3 }} more
            </span>
          </div>

          <div class="order-footer">
            <div class="order-total">
              <span class="total-label">Total:</span>
              <span class="total-amount">${{ formatPrice(order.total_cents) }}</span>
            </div>
            <button class="btn-view" @click.stop="viewOrder(order.id)">
              View Details →
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProductRepository from '../data/product.repository'

export default {
  name: 'MyOrders',
  data () {
    return {
      orders: [],
      loading: false,
      error: null
    }
  },
  mounted () {
    this.loadOrders()
  },
  methods: {
    loadOrders () {
      this.loading = true
      this.error = null

      ProductRepository.getUserOrders()
        .then((orders) => {
          this.orders = orders
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          this.error = 'Failed to load orders. Please try again later.'
          console.error('Error loading orders:', err)
        })
    },
    viewOrder (orderId) {
      this.$router.push(`/order-confirmation/${orderId}`)
    },
    getPrimaryImage (product) {
      if (!product || !product.images || product.images.length === 0) return null
      return product.images.find(img => img.is_primary === 1) || product.images[0]
    },
    formatPrice (cents) {
      return (cents / 100).toFixed(2)
    },
    formatDate (dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatStatus (status) {
      return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')
    }
  }
}
</script>

<style scoped>
.my-orders-page {
  padding: 20px 0;
  min-height: 60vh;
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-header h1 {
  font-size: 32px;
  font-weight: bold;
  margin: 0 0 8px 0;
  color: #2d3748;
}

.page-header p {
  font-size: 16px;
  color: #718096;
  margin: 0;
}

.loading {
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
  color: #718096;
}

.error-state {
  text-align: center;
  padding: 60px 20px;
}

.error-icon {
  font-size: 60px;
  margin-bottom: 20px;
}

.error-state p {
  font-size: 18px;
  color: #e53e3e;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.empty-state h2 {
  font-size: 24px;
  margin: 0 0 12px 0;
  color: #2d3748;
}

.empty-state p {
  font-size: 16px;
  color: #718096;
  margin: 0 0 24px 0;
}

.btn-shop {
  background: #3182ce;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-shop:hover {
  background: #2c5aa0;
}

.orders-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.order-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 16px;
}

.order-info h3 {
  font-size: 20px;
  margin: 0 0 6px 0;
  color: #2d3748;
}

.order-date {
  font-size: 14px;
  color: #718096;
  margin: 0;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
}

.status-badge.paid {
  background: #d4edda;
  color: #155724;
}

.status-badge.pending {
  background: #fff3cd;
  color: #856404;
}

.order-items-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.item-preview {
  display: inline-block;
}

.item-image-small {
  width: 50px;
  height: 50px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-image-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image-small {
  color: #a0aec0;
  font-size: 12px;
}

.more-items {
  font-size: 14px;
  color: #718096;
  margin-left: 4px;
}

.order-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.order-total {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.total-label {
  font-size: 14px;
  color: #718096;
}

.total-amount {
  font-size: 20px;
  font-weight: bold;
  color: #2d3748;
}

.btn-view {
  background: #3182ce;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-view:hover {
  background: #2c5aa0;
}

@media (max-width: 768px) {
  .page-header h1 {
    font-size: 24px;
  }

  .order-footer {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .btn-view {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// MyOrders.vue?6ef2ff12